import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import _pick from 'lodash/pick';
import { useLocalStorage } from 'usehooks-ts';
import { ApolloError } from '@apollo/client/errors';
import {
  Task,
  TaskCompletionStatusEnum,
  TaskScopeNameEnum,
  useTaskCompletionStatusUpdateMutation,
} from 'graphql-common';
import usePaginationParams from '@lib/hooks/usePaginationParams';
import useSearchParam from '@lib/hooks/useSearchParam';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import toast from '@lib/components/Toast/Toast';
import { GetUrlParams } from '@lib/enums/urls';
import { TaskCounter } from '@lib/interfaces/task';
import useTabParam from '@lib/hooks/useTabParam';
import usePrevious from '@lib/hooks/usePrevious';
import { Value, Values } from '@lib/interfaces/form';
import { DASHBOARD_OVERVIEW_FILTER_FORM } from '@lib/enums/localStorageKeys';
import useWhoamiQueryHook from 'utils/fetch/hooks/useWhoamiQueryHook';
import TasksFilterFieldNames from '@lib/enums/fieldNames/tasksFilterFieldNames';
import { getFormattedDate, transformDate } from '@lib/utils/dateTimeHelpers';
import { dateFormatForApi } from '@lib/enums/dateTime';
import { APP_URLS } from 'constants/urls';
import useDashboardOverviewTasksQueryHook from 'utils/fetch/hooks/useDashboardOverviewTasksQueryHook';
import dayjs, { Dayjs } from 'dayjs';
import { TASKS_OVERVIEW_FILTER_EMPTY_VALUES } from './useFilterFields';

type OnResultStateChangeHandler = (
  taskId: string,
  status: TaskCompletionStatusEnum,
) => void;

export type UseActionsResult = {
  basePeriodEndParam: string;
  basePeriodStartParam: string;
  filterParams: Values;
  onCreateIntervention: (item?: Task) => void;
  onResultStateChangeHandler: OnResultStateChangeHandler;
  onUpdateFilterParams: (values: Values) => void;
  onViewHandler: (task?: Task) => void;
  onViewIntervention: (item?: Task) => void;
  periodEndParam: string | null | undefined;
  periodStartParam: string | null | undefined;
  prevTabParam: TaskScopeNameEnum | string | undefined;
  resetFilterParams: () => void;
  resetPeriodFilterParams: () => void;
  setTabParam: (tab: TaskScopeNameEnum | string) => void;
  tabParam: TaskScopeNameEnum | string | undefined;
  taskCounterData: TaskCounter;
  taskResultStateUpdateError: unknown;
  taskResultStateUpdateLoading: boolean;
  tasks: Task[];
  tasksError?: ApolloError;
  tasksFirstLoading: boolean;
  tasksLoading: boolean;
  totalTasksCount: number;
  defaultMinDateObj?: Dayjs;
  defaultMaxDateObj?: Dayjs;
};

function getDataFromFilterParams(
  filterParams: Values,
  defaultMinDate: string,
  defaultMaxDate: string,
) {
  const basePeriod = filterParams[TasksFilterFieldNames.Period] as string[];
  const basePeriodStartParam = basePeriod?.[0] || defaultMinDate;
  const basePeriodEndParam = basePeriod?.[1] || defaultMaxDate;
  const periodStartParam = getFormattedDate(
    basePeriodStartParam,
    dateFormatForApi,
  );
  const periodEndParam = getFormattedDate(basePeriodEndParam, dateFormatForApi);

  return {
    basePeriodEndParam,
    basePeriodStartParam,
    periodEndParam,
    periodStartParam,
  };
}

export default function useActions(): UseActionsResult {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchQuery] = useSearchParam();
  const [filterParams, setFilterParams] = useLocalStorage<{
    [key: string]: Value;
  }>(DASHBOARD_OVERVIEW_FILTER_FORM, TASKS_OVERVIEW_FILTER_EMPTY_VALUES);
  const [paginationParams, setPaginationParams] = usePaginationParams();
  const [tabParam, setTabParam] = useTabParam({
    defaultTab: TaskScopeNameEnum.DashboardAll,
  });
  const prevTabParam = usePrevious(tabParam);

  // Whoami
  const { currentUser } = useWhoamiQueryHook({
    fetchPolicy: 'cache-only',
  });

  // Data
  const defaultMinDate = _get(currentUser, 'company.createdAt') as string;
  const defaultMaxDate = useMemo(() => new Date().toISOString(), []);
  const defaultMinDateObj = defaultMinDate
    ? dayjs(defaultMinDate).startOf('day')
    : undefined;
  const defaultMaxDateObj = defaultMaxDate
    ? dayjs(defaultMaxDate).endOf('day')
    : undefined;
  const defaultMinDateStr = defaultMinDateObj
    ? transformDate(defaultMinDateObj.toDate())
    : undefined;
  const defaultMaxDateStr = defaultMaxDateObj
    ? transformDate(defaultMaxDateObj.toDate(), true)
    : undefined;
  const {
    basePeriodStartParam,
    basePeriodEndParam,
    periodStartParam,
    periodEndParam,
  } = getDataFromFilterParams(filterParams, defaultMinDate, defaultMaxDate);

  // Tasks
  const {
    refetchTasks,
    taskCounterData,
    tasks,
    tasksError,
    tasksFirstLoading,
    tasksLoading,
    totalTasksCount,
  } = useDashboardOverviewTasksQueryHook({
    searchQuery,
    filterParams,
    paginationParams,
    tabParam,
    defaultMinDate: defaultMinDateStr,
    defaultMaxDate: defaultMaxDateStr,
  });

  // Bulk Scheduling
  const [taskResultStateUpdateMutation, taskResultStateUpdateResult] =
    useTaskCompletionStatusUpdateMutation({
      onCompleted: ({ data: responseData }) => {
        if (responseData) {
          toast({ content: t('task-result-state-successfully-updated') });
        }
        refetchTasks();
      },
      onError: (error) => showGraphQLErrors(error, t),
    });
  const {
    error: taskResultStateUpdateError,
    loading: taskResultStateUpdateLoading,
  } = taskResultStateUpdateResult;

  // Actions
  const onViewHandler = (task?: Task) => {
    if (task) {
      const { id } = task;
      navigate(
        APP_URLS.dashboard.tasks.view.getDynamicPath({
          pathParts: { id },
        }),
      );
    }
  };

  const onResultStateChangeHandler = (
    taskId: string,
    status: TaskCompletionStatusEnum,
  ) => {
    if (taskId && status) {
      taskResultStateUpdateMutation({
        variables: { taskId, attributes: { status } },
      });
    }
  };

  const onCreateIntervention = (item?: Task) => {
    navigate(
      APP_URLS.dashboard.interventions.add.getPathWithQuery({
        [GetUrlParams.AssetId]: item?.assetId,
        [GetUrlParams.SiteAreaId]: item?.siteArea?.id,
        [GetUrlParams.SiteId]: item?.site?.id,
        [GetUrlParams.TaskId]: item?.id,
      }),
    );
  };

  const onViewIntervention = (item?: Task) => {
    const interventionId = _get(item, 'intervention.id');
    if (interventionId) {
      navigate(
        APP_URLS.dashboard.interventions.view.getDynamicPath({
          pathParts: { id: interventionId },
        }),
      );
    }
  };

  const resetFilterParams = useCallback(() => {
    setFilterParams({
      ...TASKS_OVERVIEW_FILTER_EMPTY_VALUES,
      ..._pick(filterParams, TasksFilterFieldNames.Period),
    });
    setPaginationParams({ ...paginationParams, [GetUrlParams.Page]: '1' });
  }, [filterParams, paginationParams, setFilterParams, setPaginationParams]);

  const resetPeriodFilterParams = useCallback(() => {
    setFilterParams({
      ...filterParams,
      [TasksFilterFieldNames.Period]: [defaultMinDateStr, defaultMaxDateStr],
    });
    setPaginationParams({ ...paginationParams, [GetUrlParams.Page]: '1' });
  }, [
    defaultMinDateStr,
    defaultMaxDateStr,
    filterParams,
    paginationParams,
    setFilterParams,
    setPaginationParams,
  ]);

  const onUpdateFilterParams = (values: Values) => {
    setFilterParams(values);
    setPaginationParams({ ...paginationParams, [GetUrlParams.Page]: '1' });
  };

  return {
    basePeriodEndParam,
    basePeriodStartParam,
    filterParams,
    defaultMinDateObj,
    defaultMaxDateObj,
    onCreateIntervention,
    onResultStateChangeHandler,
    onUpdateFilterParams,
    onViewHandler,
    onViewIntervention,
    periodEndParam,
    periodStartParam,
    prevTabParam,
    resetFilterParams,
    resetPeriodFilterParams,
    setTabParam,
    tabParam,
    taskCounterData,
    taskResultStateUpdateError,
    taskResultStateUpdateLoading,
    tasks,
    tasksError,
    tasksFirstLoading,
    tasksLoading,
    totalTasksCount,
  };
}
